import { ClearOutlined } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { useRef, useState } from 'react';
import type { RegisterOptions } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';

import type { VNFieldHelpProps } from '@/components/VNFieldHelp';
import { hasFieldHelpIcon, VNFieldHelpLayout, VNFieldTopLabel } from '@/components/VNFieldHelp';

import ContextMenuWithSubmenu from './ContextMenuWithSubmenu';

type PlaceholderItem = string;
type PlaceholderGroup = {
  label: string;
  items: (PlaceholderItem | PlaceholderGroup)[];
};

type Props = VNFieldHelpProps & {
  name: string;
  label?: string;
  disabled?: boolean;
  rows?: number;
  tooltip?: string;
  rules?: RegisterOptions;
  required?: boolean;
  defaultValue?: string | number;
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  /** Fired with the new string value after the form value is updated. */
  onValueChange?: (value: string) => void;
  type?: 'text' | 'number' | 'textarea' | 'password' | 'time';
  shrinkLabel?: boolean;
  placeholders?: (PlaceholderItem | PlaceholderGroup)[];
  InputProps?: React.ComponentProps<typeof TextField>['InputProps'];
  placeholder?: string;
  allowClear?: boolean;
  hiddenLabel?: boolean;
  autoComplete?: string;
  /** false (default) = floating label; true = static label above the control. */
  ver2?: boolean;
};

const VNTextField = ({
  name,
  label = '',
  disabled = false,
  rows = 0,
  tooltip = '',
  rules,
  required = false,
  defaultValue,
  type,
  onFocus,
  onBlur,
  onKeyDown,
  onValueChange,
  shrinkLabel = false,
  placeholders = [],
  InputProps,
  placeholder,
  allowClear = false,
  hiddenLabel = false,
  autoComplete,
  ver2 = false,
  ...fieldHelpProps
}: Props) => {
  const { control, watch } = useFormContext();
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const showFieldHelp = hasFieldHelpIcon(fieldHelpProps);
  const suppressFloating = hiddenLabel || ver2;
  const watchedValue = watch(name);
  const showClear = Boolean(
    allowClear && (Array.isArray(watchedValue) ? watchedValue.length > 0 : watchedValue != null && watchedValue !== '')
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (onKeyDown) {
      return onKeyDown(e);
    }

    if (type === 'number') {
      const { key } = e;
      const isNumberKey = /^[0-9]$/.test(key);
      const isDecimal = key === '.' || key === ',';
      const isControls = [
        'Backspace',
        'Delete',
        'Enter',
        'Shift',
        'Control',
        'Alt',
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
        'Tab',
      ].includes(key);

      if (!isNumberKey && !isDecimal && !isControls) {
        e.preventDefault();
        return false;
      }
    }
    return true;
  };

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);

  const handleContextMenu = (event: React.MouseEvent) => {
    if (!placeholders.length) return;
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setMenuPosition({ top: event.clientY, left: event.clientX });
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuPosition(null);
  };

  const handleInsert = (insertText: string, value: string, onChange: (v: string) => void) => {
    handleCloseMenu();
    if (!inputRef.current) return;

    const cursor = inputRef.current.selectionStart ?? value.length;
    const before = value.slice(0, cursor);
    const after = value.slice(cursor);
    const newValue = `${before}${insertText}${after}`;
    onChange(newValue);

    requestAnimationFrame(() => {
      if (inputRef.current) {
        const newPos = cursor + insertText.length;
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newPos, newPos);
      }
    });
  };

  const controller = (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required && 'This is a required field',
        ...(type === 'number' && {
          min: { value: 0, message: 'Value must be a positive number' },
        }),
        ...rules,
      }}
      defaultValue={defaultValue}
      render={({ field: { value, onChange, ...field }, fieldState }) => {
        const isArray = Array.isArray(value);
        const textValue = isArray ? value.join(', ') : (value as string) || '';

        const textField = (
          <div className="vn-textfield__field-wrap">
            <TextField
              variant="outlined"
              size="small"
              {...field}
              id={name}
              hiddenLabel={suppressFloating}
              label={suppressFloating ? undefined : `${label}${required ? ' *' : ''}`}
              disabled={disabled}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              multiline={rows > 0}
              rows={rows}
              type={type}
              value={textValue}
              onBlur={onBlur}
              onFocus={onFocus}
              onContextMenu={handleContextMenu}
              onChange={(e) => {
                const newValue = e.target.value;
                onChange(isArray ? newValue.split(',').map((item) => item.trim()) : newValue);
                onValueChange?.(newValue);
              }}
              onKeyDown={handleKeyDown}
              autoComplete={autoComplete}
              InputLabelProps={shrinkLabel ? { shrink: true } : {}}
              inputRef={inputRef}
              InputProps={{
                ...InputProps,
                endAdornment:
                  allowClear && textValue ? (
                    <InputAdornment position="end" className="vn-textfield__clear-adornment">
                      <IconButton
                        size="small"
                        className="vn-textfield-clear"
                        onClick={() => onChange(isArray ? [] : '')}
                        aria-label="Clear"
                      >
                        <ClearOutlined fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : (
                    InputProps?.endAdornment
                  ),
              }}
              placeholder={placeholder ?? (placeholders.length === 1 ? (placeholders[0] as PlaceholderItem) : '')}
            />
            <ContextMenuWithSubmenu
              anchorEl={anchorEl}
              position={menuPosition}
              onClose={handleCloseMenu}
              placeholders={placeholders}
              onInsert={(ph) => handleInsert(ph, textValue, onChange)}
            />
          </div>
        );

        if (!showFieldHelp && tooltip) {
          return (
            <Tooltip title={tooltip} placement="top-end" arrow disableInteractive>
              <div className="vn-textfield__tooltip-wrap">{textField}</div>
            </Tooltip>
          );
        }

        return textField;
      }}
    />
  );

  return (
    <div
      className={['vn-textfield', ver2 ? 'vn-field--label-above' : '', showClear ? 'vn-textfield--clear' : '']
        .filter(Boolean)
        .join(' ')}
    >
      {ver2 && label ? <VNFieldTopLabel label={label} required={required} htmlFor={name} /> : null}
      {showFieldHelp ? (
        <VNFieldHelpLayout label={label} required={required} {...fieldHelpProps}>
          {controller}
        </VNFieldHelpLayout>
      ) : (
        controller
      )}
    </div>
  );
};

export default VNTextField;
